import { useState, useEffect, useRef } from 'react';

// Module-level cache for Plotly - shared across all components
let plotlyModule: typeof import('plotly.js-dist-min') | null = null;
let plotlyPromise: Promise<typeof import('plotly.js-dist-min')> | null = null;

/**
 * Hook for lazy-loading Plotly.js
 *
 * Plotly is a large library (~1.5MB gzipped) that shouldn't be loaded
 * until a widget that needs it is actually rendered.
 *
 * Usage:
 * const { Plotly, isLoading, error } = usePlotly();
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage />;
 *
 * // Now you can use Plotly
 * Plotly.newPlot(...)
 */
export function usePlotly() {
  const [isLoading, setIsLoading] = useState(!plotlyModule);
  const [error, setError] = useState<Error | null>(null);
  const [Plotly, setPlotly] = useState<typeof import('plotly.js-dist-min') | null>(plotlyModule);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    // If already loaded, we're done
    if (plotlyModule) {
      setPlotly(plotlyModule);
      setIsLoading(false);
      return;
    }

    // If already loading, wait for it
    if (plotlyPromise) {
      plotlyPromise
        .then((module) => {
          if (isMounted.current) {
            setPlotly(module);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (isMounted.current) {
            setError(err);
            setIsLoading(false);
          }
        });
      return;
    }

    // Start loading
    plotlyPromise = import('plotly.js-dist-min');

    plotlyPromise
      .then((module) => {
        plotlyModule = module;
        if (isMounted.current) {
          setPlotly(module);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        plotlyPromise = null; // Allow retry on error
        if (isMounted.current) {
          setError(err);
          setIsLoading(false);
        }
      });

    return () => {
      isMounted.current = false;
    };
  }, []);

  return { Plotly, isLoading, error };
}
